import { AlertTriangle } from "lucide-react";

/**
 * TrashPurgeBanner — bannière d'alerte pour la Corbeille (Tâches & Documents).
 *
 * Affiche discrètement le nombre d'éléments dont la purge automatique (30j)
 * approche. Se contente d'un rendu si au moins un élément a ≤ 3 jours avant
 * suppression définitive. Ton warning amber, aligné dark-mode Vertec.
 *
 * Props:
 *   items       — array d'items ({ deleted_at, ... }) tels que renvoyés par
 *                 l'API `/tasks/trash/list` ou `/documents/trash/list`.
 *   noun        — "tâche" | "document"  (accord grammatical géré ci-dessous)
 *   nounFemale  — bool pour "supprimée(s)" vs "supprimé(s)"
 *   thresholdDays — 3 par défaut ; horizon de préavis en jours.
 *   retentionDays — 30 par défaut ; doit matcher le scheduler.
 */
export default function TrashPurgeBanner({
  items = [],
  noun = "élément",
  nounFemale = false,
  thresholdDays = 3,
  retentionDays = 30,
}) {
  const now = Date.now();
  const soon = items.filter((it) => {
    if (!it?.deleted_at) return false;
    const deletedMs = new Date(it.deleted_at).getTime();
    if (Number.isNaN(deletedMs)) return false;
    const purgeMs = deletedMs + retentionDays * 24 * 60 * 60 * 1000;
    const daysLeft = (purgeMs - now) / (24 * 60 * 60 * 1000);
    return daysLeft > 0 && daysLeft <= thresholdDays;
  });
  if (soon.length === 0) return null;

  const n = soon.length;
  const singular = n === 1;
  const plural = singular ? "" : "s";
  const suffix = nounFemale ? "e" : "";
  const verb = singular ? "sera" : "seront";
  const pronoun = singular ? (nounFemale ? "la" : "le") : "les";

  return (
    <div
      className="flex items-start gap-3 rounded-lg px-4 py-3 border"
      style={{
        borderColor: "rgba(245, 200, 66, 0.35)",   // #F5C842 warning yellow @ 35%
        background: "linear-gradient(90deg, rgba(245,200,66,0.10) 0%, rgba(245,200,66,0.02) 100%)",
      }}
      data-testid="trash-purge-banner"
    >
      <AlertTriangle
        size={18}
        strokeWidth={2.2}
        style={{ color: "#F5C842", flexShrink: 0, marginTop: 2 }}
      />
      <div className="text-sm leading-relaxed">
        <span className="font-semibold" style={{ color: "#F5C842" }}>
          {n} {noun}{plural} {verb} supprimé{suffix}{plural} définitivement
        </span>{" "}
        <span className="text-muted-foreground">
          dans les {thresholdDays} prochains jours. Restaurez-{pronoun} si nécessaire avant l'échéance.
        </span>
      </div>
    </div>
  );
}
